/* eslint-disable no-console */
import type {
  ConnectionCheckMessage,
  OrderUpdateReceivedMessage,
  StateUpdateReceivedMessage,
  UpdateOrderMessage,
  UpdateStateMessage,
  UpdateWaitTimeMessage,
  WaitTimeUpdateReceivedMessage,
} from './api.websocket.types'
import type { OrderDetails } from '@/entities/order/model'
import type { AppRegion } from '@/entities/region'

import * as Sentry from '@sentry/react'
import { useConnectionStatusStore } from '@/shared/lib/connection-status'
import { WebSocketMetrics } from '@/shared/lib/websocket'
import { getWebsocketUrl } from './api.lib'

/**
 * WebSocket Logging Configuration:
 *
 * Production logging is minimal by default (errors only).
 * Development: All WebSocket events are logged to console for debugging.
 */

// --- Constants ---
const RECONNECTION_ATTEMPT_MINIMUM_TIME = 1000 * 2 // 2 seconds
const RECONNECTION_MAX_DELAY = 1000 * 10 // 10 seconds
const RECONNECTION_BACKOFF_MULTIPLIER = 2
const CONNECTION_CHECK_SEND_TIMER = 1000 * 20 // 20 seconds
const CONNECTION_TIMEOUT = 1000 * 10 // 10 seconds
const WEBVIEW_INIT_DELAY = 1000 * 1.5 // 1.5 seconds

// --- Module State ---
let websocket: WebSocket | null = null
let isAttemptingConnection = false
let currentToken: string | null = null
let shouldBeConnected = false // Flag to indicate if we should actively try to connect
let lastEventSendTime: string = ''
let lastReceivedTime: string = ''
let webViewInitDelayApplied = false // Flag to limit WebView delay to first-ever initial connection
let webViewDetectedCached: boolean | null = null // Cache WebView detection result to log once
let currentRegion: string | null = null // Persist region for Sentry tagging and grouping

// Timer management - centralized handling for all WebSocket timers
// Each timer is tracked by key and can be cleared individually or in bulk
type TimerKey = 'reconnect' | 'heartbeat' | 'connectionTimeout' | 'webViewInit'
const TimerManager = {
  timers: {
    reconnect: null as NodeJS.Timeout | null,
    heartbeat: null as NodeJS.Timeout | null,
    connectionTimeout: null as NodeJS.Timeout | null,
    webViewInit: null as NodeJS.Timeout | null,
  },
  setTimer(key: TimerKey, delay: number, callback: () => void): NodeJS.Timeout {
    this.clearTimer(key)
    const timerId = setTimeout(callback, delay)
    this.timers[key] = timerId
    return timerId
  },
  clearTimer(key: TimerKey): void {
    if (this.timers[key]) {
      clearTimeout(this.timers[key]!)
    }
    this.timers[key] = null
  },
  clearAllTimers(): void {
    (Object.keys(this.timers) as TimerKey[]).forEach((key) => {
      this.clearTimer(key)
    })
  },
}

// Connection metrics - instantiate class for tracking performance and health
const metrics = new WebSocketMetrics()

let onOrderUpdate: ((order: OrderDetails) => void) | null = null
let onStateUpdate: (() => void) | null = null
let onWaitTimeUpdate: ((waitTimeData: { waitTimeAverage: number, numberOfOrders: number }) => void) | null = null
let onInitialSync: (() => Promise<void>) | null = null

// --- Helper Functions ---

// WebSocket detection - cached to log once
function isWebView(): boolean {
  if (webViewDetectedCached !== null) {
    return webViewDetectedCached
  }
  // Guard against SSR/test environments where navigator is undefined
  if (typeof navigator === 'undefined') {
    webViewDetectedCached = false
    return false
  }
  const userAgent = navigator.userAgent
  // Android WebView: Check for 'wv' or Android with Version/ pattern
  const isAndroidWebView = /wv|Android.*Version\//i.test(userAgent)
  // iOS WebView: Check for iPhone/iPad without Safari
  const isIosWebView = /iPhone|iPad/.test(userAgent) && !/Safari/.test(userAgent)
  const detected = isAndroidWebView || isIosWebView
  webViewDetectedCached = detected
  if (detected && import.meta.env.DEV) {
    console.log('[WS] WebView environment detected')
  }
  return detected
}

function calculateDuration(startTime: number | null, endTime: number | null): number | null {
  if (startTime === null || endTime === null) {
    return null
  }
  return endTime - startTime
}

// Sentry logging helpers
function addSentryBreadcrumb(eventName: string, data: Record<string, any>): void {
  try {
    const client = Sentry.getClient()
    if (!client) {
      return
    }

    // Determine breadcrumb level based on event type
    let level: 'info' | 'warning' | 'error' = 'info'
    if (eventName.includes('error') || eventName.includes('timeout') || eventName.includes('failed')) {
      level = 'error'
    } else if (eventName.includes('reconnection') || eventName.includes('closed')) {
      level = 'warning'
    }

    Sentry.addBreadcrumb({
      category: 'websocket',
      message: eventName,
      level,
      data: { ...data, 'ws.region': currentRegion || 'unknown' },
      timestamp: Date.now() / 1000,
    })
  } catch {
    // Fail silently to ensure analytics errors don't break WebSocket
  }
}

function captureSentryMessage(eventName: string, data: Record<string, any>): void {
  try {
    const client = Sentry.getClient()
    if (!client) {
      return
    }

    // Capture error objects with full stack traces (preferred over message for Error instances)
    if (data.error instanceof Error) {
      const stats = metrics.getStats()
      Sentry.captureException(data.error, {
        tags: { 'ws.event': eventName, 'ws.type': 'error', 'ws.region': currentRegion || 'unknown' },
        fingerprint: ['websocket', eventName],
        extra: {
          wsReadyState: websocket?.readyState,
          lastFailure: stats.lastFailureReason,
          totalAttempts: stats.totalAttempts,
        },
      })
      // Add breadcrumb for trace visibility without double-capturing
      addSentryBreadcrumb(eventName, data)
      return
    }

    // Capture message for significant events (only when no Error object is present)
    const significantEvents = [
      'url_generation_error',
      'instantiation_error',
      'connection_timeout',
      'heartbeat_failed',
      'initial_sync_failed',
      'message_parsing_error',
      'callback_missing',
    ]
    if (significantEvents.includes(eventName)) {
      const messageLevel = eventName === 'connection_established' ? 'info' : 'error'
      const stats = metrics.getStats()
      Sentry.captureMessage(`WebSocket: ${eventName}`, {
        level: messageLevel,
        tags: { 'ws.event': eventName, 'ws.type': messageLevel, 'ws.region': currentRegion || 'unknown' },
        fingerprint: ['websocket', eventName],
        extra: {
          wsReadyState: websocket?.readyState,
          lastFailure: stats.lastFailureReason,
          isConnected: websocket?.readyState === WebSocket.OPEN,
        },
      })
    }
  } catch {
    // Fail silently to ensure analytics errors don't break WebSocket
  }
}

let lastReconnectionLogTime = 0
const RECONNECTION_LOG_THROTTLE_MS = 3000 // Rate-limit reconnection logs to once every 3 seconds

// --- Test-Only Reset Function ---
/**
 * INTERNAL TEST USE ONLY: Resets all module-level state to initial values.
 * This function is used by the test suite to isolate tests and prevent state leakage.
 * @internal
 */
export function __resetWebSocketModuleState__(): void {
  // Close existing connection without triggering reconnect
  if (websocket) {
    websocket.onopen = null
    websocket.onmessage = null
    websocket.onclose = null
    websocket.onerror = null
    try {
      websocket.close(1001, 'Test reset')
    } catch {
      // Ignore errors during reset
    }
  }

  // Reset all module state to initial values
  websocket = null
  isAttemptingConnection = false
  currentToken = null
  shouldBeConnected = false
  lastEventSendTime = ''
  lastReceivedTime = ''
  webViewInitDelayApplied = false
  webViewDetectedCached = null
  currentRegion = null

  // Clear all timers
  TimerManager.clearAllTimers()

  // Reset metrics
  metrics.reset()

  // Clear callbacks
  onOrderUpdate = null
  onStateUpdate = null
  onWaitTimeUpdate = null
  onInitialSync = null

  // Reset reconnection log throttle
  lastReconnectionLogTime = 0
}

/**
 * INTERNAL TEST USE ONLY: Sets specific state properties for edge case testing.
 * @internal
 */
export function __setWebSocketInternalState__(state: { shouldBeConnected?: boolean, currentToken?: string | null }): void {
  if (state.shouldBeConnected !== undefined) {
    shouldBeConnected = state.shouldBeConnected
  }
  if (state.currentToken !== undefined) {
    currentToken = state.currentToken
  }
}

function logConnectionMetric(eventName: string, data: Record<string, any>) {
  // Determine severity based on event name (same logic as addSentryBreadcrumb)
  let severity: 'error' | 'warning' | 'info' = 'info'
  if (eventName.includes('error') || eventName.includes('timeout') || eventName.includes('failed')) {
    severity = 'error'
  } else if (eventName.includes('reconnection') || eventName.includes('closed')) {
    severity = 'warning'
  }
  const isErrorEvent = severity === 'error' || severity === 'warning'

  // Rate-limit reconnection logs in production to reduce noise
  if (eventName === 'reconnection_scheduled') {
    const now = Date.now()
    if (!import.meta.env.DEV && now - lastReconnectionLogTime < RECONNECTION_LOG_THROTTLE_MS) {
      // Skip logging this reconnection event, but preserve breadcrumb
      try {
        const isAuthenticated = currentToken !== null
        const webViewDetected = isWebView()
        const enrichedData = { ...data, isAuthenticated, webViewDetected }
        addSentryBreadcrumb(eventName, enrichedData)
      } catch {
        // Fail silently to ensure analytics errors don't break WebSocket
      }
      return
    }
    lastReconnectionLogTime = now
  }

  // Log to console in development mode or for error events
  if (import.meta.env.DEV || isErrorEvent) {
    const timestamp = new Date().toISOString()
    const message = `[WS] ${timestamp} ${eventName}: ${JSON.stringify(data)}`
    if (severity === 'error') {
      console.error(message)
    } else if (severity === 'warning') {
      console.warn(message)
    } else {
      console.info(message)
    }
  }

  try {
    // Add authentication context if available
    const isAuthenticated = currentToken !== null
    const webViewDetected = isWebView()
    const enrichedData = { ...data, isAuthenticated, webViewDetected }

    addSentryBreadcrumb(eventName, enrichedData)
    captureSentryMessage(eventName, enrichedData)
  } catch {
    // Fail silently to ensure analytics errors don't break WebSocket
  }
}

function updateRealtimeStatus(isConnected: boolean) {
  // Use the store's action to update the global state
  useConnectionStatusStore.getState().setRealtimeConnected(isConnected)
}

function scheduleReconnect() {
  TimerManager.clearTimer('reconnect')
  if (isAttemptingConnection || !shouldBeConnected)
    return // Don't schedule if already trying or shouldn't be connected

  const stats = metrics.getStats()
  // Calculate exponential backoff delay: base * (multiplier ^ attemptCount), e.g., 2s, 4s, 8s, 16s...
  // This prevents overwhelming the server with reconnection attempts
  const exponentialDelay = RECONNECTION_ATTEMPT_MINIMUM_TIME * (RECONNECTION_BACKOFF_MULTIPLIER ** stats.backoffAttemptCount)
  // Cap at maximum to avoid excessive delays
  const delay = Math.min(exponentialDelay, RECONNECTION_MAX_DELAY)

  metrics.update({ backoffAttemptCount: stats.backoffAttemptCount + 1, totalReconnections: stats.totalReconnections + 1 })

  const updatedStats = metrics.getStats()
  logConnectionMetric('reconnection_scheduled', {
    delay,
    attemptNumber: updatedStats.backoffAttemptCount,
    totalReconnections: updatedStats.totalReconnections,
    lastFailureReason: updatedStats.lastFailureReason,
  })

  TimerManager.setTimer('reconnect', delay, () => {
    // Use stored credentials for reconnect attempt
    if (currentToken) {
      attemptConnection(currentToken)
    } else {
      // Log: Critical - reconnection failure indicates auth/network issues
      console.error('[WS] ERROR: Cannot reconnect - authentication token missing')
    }
  })
}

function scheduleHeartbeat() {
  TimerManager.clearTimer('heartbeat')

  TimerManager.setTimer('heartbeat', CONNECTION_CHECK_SEND_TIMER, () => {
    if (websocket?.readyState === WebSocket.OPEN) {
      const connectionCheckMessage: ConnectionCheckMessage = {
        messageType: 'CONNECTION_CHECK',
        timestamp: new Date().getTime(),
      }
      try {
        websocket.send(JSON.stringify(connectionCheckMessage))
        lastEventSendTime = new Date().toISOString()
        // Schedule next heartbeat
        scheduleHeartbeat()
      } catch (err) {
        // Log: Critical - heartbeat failure may indicate server issues
        console.error('[WS] ERROR: Heartbeat send failed')
        logConnectionMetric('heartbeat_failed', { error: err })
      }
    }
  })
}

// --- Event Handlers ---
function handleOpen(_event: Event) {
  TimerManager.clearTimer('connectionTimeout')
  const stats = metrics.getStats()
  const connectionDuration = calculateDuration(stats.attemptStartTime, Date.now())
  isAttemptingConnection = false
  const isReconnection = stats.totalReconnections > 0
  metrics.update({
    establishedTime: Date.now(),
    successfulConnections: stats.successfulConnections + 1,
    backoffAttemptCount: 0, // Reset backoff counter on successful connection
    attemptStartTime: null,
  })
  const updatedStats = metrics.getStats()
  logConnectionMetric('connection_established', {
    duration: connectionDuration,
    attemptNumber: updatedStats.totalAttempts,
    successfulConnections: updatedStats.successfulConnections,
    isReconnection,
    backoffAttempts: updatedStats.backoffAttemptCount,
  })
  lastEventSendTime = new Date().toISOString()
  lastReceivedTime = new Date().toISOString()
  updateRealtimeStatus(true)

  // Start heartbeat
  scheduleHeartbeat()

  if (onInitialSync) {
    onInitialSync().catch((error) => {
      logConnectionMetric('initial_sync_failed', { error })
    })
  }
}

function handleMessage(event: MessageEvent) {
  try {
    const parsedMessage = JSON.parse(event.data)
    lastReceivedTime = new Date().toISOString()

    // Track time to first message after connection - useful for measuring backend responsiveness
    // This helps identify if the server is slow to send initial data after connection
    const stats = metrics.getStats()
    if (stats.firstMessageTime === null && stats.establishedTime !== null) {
      metrics.update({ firstMessageTime: Date.now() })
      const updatedStats = metrics.getStats()
      const timeToFirstMessage = calculateDuration(stats.establishedTime, updatedStats.firstMessageTime)
      logConnectionMetric('first_message_received', {
        timeToFirstMessage,
        messageType: parsedMessage.messageType,
      })
    }

    if (parsedMessage.messageType === 'ORDER_UPDATE') {
      const orderUpdateMessage = parsedMessage as UpdateOrderMessage
      if (import.meta.env.DEV) {
        console.info(`[WS] 🌐 ORDER_UPDATE received: orderId=${orderUpdateMessage.order.orderId}, status=${orderUpdateMessage.order.status}`)
      }

      if (onOrderUpdate) {
        onOrderUpdate(orderUpdateMessage.order)
      } else {
        // Log: Critical - missing callback prevents order processing
        console.error('[WS] ERROR: ORDER_UPDATE callback missing')
        logConnectionMetric('callback_missing', { messageType: 'ORDER_UPDATE' })
      }

      // Send acknowledgment
      const updateReceivedMessage: OrderUpdateReceivedMessage = {
        messageType: 'ORDER_UPDATE_RECEIVED',
        notificationId: orderUpdateMessage.notificationId,
        timestamp: new Date().toISOString(),
      }
      if (websocket?.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify(updateReceivedMessage))
      }
    } else if (parsedMessage.messageType === 'STATE_UPDATE') {
      const stateUpdateMessage = parsedMessage as UpdateStateMessage
      if (onStateUpdate) {
        onStateUpdate()
      }

      // Send acknowledgment
      const message: StateUpdateReceivedMessage = {
        messageType: 'STATE_UPDATE_RECEIVED',
        projectId: stateUpdateMessage.projectId,
        eventTime: stateUpdateMessage.eventTime,
        timestamp: new Date().toISOString(),
      }
      if (stateUpdateMessage.destinationId) {
        message.destinationId = stateUpdateMessage.destinationId
      }
      if (websocket?.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify(message))
      }
    } else if (parsedMessage.messageType === 'WAIT_TIME_UPDATE') {
      const waitTimeUpdateMessage = parsedMessage as UpdateWaitTimeMessage
      if (import.meta.env.DEV) {
        console.info(`[WS] 🌐 WAIT_TIME_UPDATE received: avgWait=${waitTimeUpdateMessage.waitTimeAverage}s, orderCount=${waitTimeUpdateMessage.numberOfOrders}`)
      }

      if (onWaitTimeUpdate) {
        onWaitTimeUpdate({
          waitTimeAverage: waitTimeUpdateMessage.waitTimeAverage,
          numberOfOrders: waitTimeUpdateMessage.numberOfOrders,
        })
      } else {
        // Log: Critical - missing callback prevents wait time updates
        console.error('[WS] ERROR: WAIT_TIME_UPDATE callback missing')
        logConnectionMetric('callback_missing', { messageType: 'WAIT_TIME_UPDATE' })
      }

      // Send acknowledgment
      const waitTimeReceivedMessage: WaitTimeUpdateReceivedMessage = {
        messageType: 'WAIT_TIME_UPDATE_RECEIVED',
        timestamp: new Date().toISOString(),
      }
      if (websocket?.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify(waitTimeReceivedMessage))
      }
    }
  } catch (error) {
    // Log: Critical - parsing errors indicate protocol mismatch
    console.error('[WS] ERROR: Message parsing failed')
    logConnectionMetric('message_parsing_error', { error, rawMessage: event.data.substring(0, 200) })
  }
}

function handleClose(event: CloseEvent) {
  TimerManager.clearTimer('connectionTimeout')
  const stats = metrics.getStats()
  const uptime = calculateDuration(stats.establishedTime, Date.now())
  isAttemptingConnection = false
  const isCleanClose = event.code === 1000
  logConnectionMetric('connection_closed', {
    code: event.code,
    reason: event.reason || 'N/A',
    uptime,
    isCleanClose,
    willReconnect: shouldBeConnected && !isCleanClose,
  })
  metrics.update({ establishedTime: null, firstMessageTime: null })
  websocket = null // Ensure instance is cleared
  TimerManager.clearTimer('heartbeat')
  updateRealtimeStatus(false)
  // Attempt to reconnect only if we should be connected and it wasn't a clean close (like manual disconnect)
  if (shouldBeConnected && event.code !== 1000) {
    scheduleReconnect()
  }
}

function handleError(event: Event) {
  TimerManager.clearTimer('connectionTimeout')
  const reason = (event as any).type || 'unknown_error'
  const stats = metrics.getStats()
  const attemptDuration = calculateDuration(stats.attemptStartTime, Date.now())
  isAttemptingConnection = false
  metrics.update({
    lastFailureReason: reason,
    attemptStartTime: null,
    establishedTime: null,
    firstMessageTime: null,
  })
  const updatedStats = metrics.getStats()
  logConnectionMetric('connection_error', {
    reason: updatedStats.lastFailureReason,
    duration: attemptDuration,
    attemptNumber: updatedStats.totalAttempts,
    willReconnect: shouldBeConnected,
  })
  updateRealtimeStatus(false) // Assume disconnected on error
  // Schedule reconnect if we should be connected
  if (shouldBeConnected) {
    scheduleReconnect()
  }
}

// --- Internal Connection Logic ---
function attemptConnection(token: string) {
  TimerManager.clearTimer('webViewInit')

  if (websocket && websocket.readyState !== WebSocket.CLOSED) {
    if (import.meta.env.DEV) {
      console.warn('[WS] Closing existing connection before new attempt')
    }
    // Remove listeners to prevent interference during close
    websocket.onopen = null
    websocket.onmessage = null
    websocket.onclose = null
    websocket.onerror = null
    websocket.close(1001, 'Client re-initiating connection')
  }

  isAttemptingConnection = true
  const stats = metrics.getStats()
  metrics.update({
    attemptStartTime: Date.now(),
    totalAttempts: stats.totalAttempts + 1,
    firstMessageTime: null,
  })
  const updatedStats = metrics.getStats()
  logConnectionMetric('connection_attempt_started', {
    attemptNumber: updatedStats.totalAttempts,
    isReconnection: updatedStats.backoffAttemptCount > 0,
    webViewDetected: isWebView(),
  })
  TimerManager.clearTimer('reconnect') // Clear pending reconnect timer

  const targetUrl = getWebsocketUrl()

  if (!targetUrl) {
    isAttemptingConnection = false
    logConnectionMetric('url_generation_error', { error: 'Failed to generate WebSocket URL' })
    // Trigger error handling (which includes potential reconnect)
    handleError(new Event('url_generation_error'))
    return
  }

  try {
    websocket = new WebSocket(targetUrl, token)
    websocket.onopen = handleOpen
    websocket.onmessage = handleMessage
    websocket.onclose = handleClose
    websocket.onerror = handleError

    // Fail fast if connection hangs - especially important in WebView environments where default timeout may be indefinite
    // Without this timeout, the connection could hang forever waiting for the server to respond
    TimerManager.clearTimer('connectionTimeout')
    TimerManager.setTimer('connectionTimeout', CONNECTION_TIMEOUT, () => {
      isAttemptingConnection = false
      const timeoutStats = metrics.getStats()
      metrics.update({ lastFailureReason: 'connection_timeout' })
      const timeoutDuration = calculateDuration(timeoutStats.attemptStartTime, Date.now())
      const updatedTimeoutStats = metrics.getStats()
      logConnectionMetric('connection_timeout', {
        timeoutDuration,
        configuredTimeout: CONNECTION_TIMEOUT,
        attemptNumber: updatedTimeoutStats.totalAttempts,
      })
      if (websocket?.readyState === WebSocket.CONNECTING) {
        websocket.close()
      }
      handleError(new Event('connection_timeout'))
    })
  } catch (error) {
    isAttemptingConnection = false
    const catchStats = metrics.getStats()
    metrics.update({ lastFailureReason: 'instantiation_error' })
    const attemptDuration = calculateDuration(catchStats.attemptStartTime, Date.now())
    const updatedCatchStats = metrics.getStats()
    logConnectionMetric('instantiation_error', {
      error,
      duration: attemptDuration,
      attemptNumber: updatedCatchStats.totalAttempts,
    })
    // Trigger error handling (which includes potential reconnect)
    handleError(new Event('instantiation_error'))
  }
}

// --- Public API ---

/**
 * Initiates and maintains a WebSocket connection for status reporting.
 *
 * This function initiates a connection for status reporting only (no message handling).
 * For full realtime functionality including order updates, use `connectWebSocketForRealtime()` instead.
 *
 * Automatic reconnection is enabled with exponential backoff. WebView environments
 * receive a 1.5s delay on the first connection attempt to allow the network stack to initialize.
 *
 * @example
 * connectWebSocketForStatus(userRegion, authToken)
 */
export function connectWebSocketForStatus(
  region: AppRegion,
  token: string,
) {
  if (!region || !token) {
    console.error('WebSocket: Cannot connect without region and token.')
    shouldBeConnected = false // Can't connect if params missing
    disconnectWebSocketForStatus() // Ensure any existing connection is closed
    return
  }

  shouldBeConnected = true // Set the intent to be connected
  // Store credentials for potential reconnects
  currentToken = token
  currentRegion = region

  if (websocket && websocket.readyState === WebSocket.OPEN) {
    if (import.meta.env.DEV) {
      console.log('[WS] Already connected - skipping connection attempt')
    }
    // Ensure status is correct in case it was missed
    updateRealtimeStatus(true)
    return
  }
  if ((websocket && websocket.readyState === WebSocket.CONNECTING) || isAttemptingConnection) {
    if (import.meta.env.DEV) {
      console.log('[WS] Connection attempt already in progress - skipping')
    }
    return
  }

  if (isWebView()) {
    if (import.meta.env.DEV) {
      console.log(`[WS] WebView detected - delaying connection by ${WEBVIEW_INIT_DELAY}ms for network stack initialization`)
    }
    TimerManager.clearTimer('webViewInit')
    // WebView network stack needs initialization time - delay first connection only, not reconnections
    // This ensures the WebView's network stack is ready before attempting the connection
    if (!webViewInitDelayApplied) {
      webViewInitDelayApplied = true
      TimerManager.setTimer('webViewInit', WEBVIEW_INIT_DELAY, () => {
        // Guard: check shouldBeConnected and use currentToken if available
        const t = currentToken
        if (!shouldBeConnected || !t) {
          return
        }
        attemptConnection(t)
      })
    } else {
      attemptConnection(token)
    }
  } else {
    attemptConnection(token)
  }
}

/**
 * Disconnects the WebSocket connection used for status reporting gracefully.
 *
 * This function performs a graceful disconnect, clears all timers, resets state,
 * and prevents automatic reconnection. Call this when realtime updates are no longer
 * needed (e.g., on logout or when the realtime feature is disabled).
 *
 * @example
 * disconnectWebSocketForStatus()
 */
export function disconnectWebSocketForStatus() {
  shouldBeConnected = false // Set the intent to be disconnected
  TimerManager.clearAllTimers() // Stop all timers

  const disconnectStats = metrics.getStats()
  if (websocket) {
    const finalUptime = calculateDuration(disconnectStats.establishedTime, Date.now())
    if (import.meta.env.DEV) {
      console.log(`[WS] Manual disconnect initiated (uptime: ${finalUptime}ms, total attempts: ${disconnectStats.totalAttempts}, successful: ${disconnectStats.successfulConnections})`)
    }
    // Remove listeners to prevent triggering reconnect logic on explicit close
    websocket.onopen = null
    websocket.onmessage = null
    websocket.onclose = null // Prevent handleClose from triggering reconnect
    websocket.onerror = null
    websocket.close(1000, 'Client disconnected status check') // Graceful disconnect
    websocket = null
    updateRealtimeStatus(false) // Ensure status is updated
  } else if (import.meta.env.DEV) {
    console.log('[WS] Already disconnected - no action needed')
    // Ensure status is correct if called redundantly
    updateRealtimeStatus(false)
  }

  logConnectionMetric('manual_disconnect', {
    totalAttempts: disconnectStats.totalAttempts,
    successfulConnections: disconnectStats.successfulConnections,
    totalReconnections: disconnectStats.totalReconnections,
  })
  isAttemptingConnection = false // Ensure flags are reset
  metrics.reset()
  webViewInitDelayApplied = false // Reset WebView delay flag for session resets - allows delay on next connection if app restarts
  webViewDetectedCached = null // Reset WebView detection cache for next session

  // Clear callbacks
  onOrderUpdate = null
  onStateUpdate = null
  onWaitTimeUpdate = null
}

/**
 * Registers callbacks that will be invoked when WebSocket receives messages.
 *
 * This function sets up callbacks for realtime updates. Call this before `connectWebSocketForStatus()`
 * if you want to receive realtime updates, or use the convenience function `connectWebSocketForRealtime()`
 * which does both in one call.
 *
 * @param orderUpdateCallback - Invoked when ORDER_UPDATE message is received with order details
 * @param stateUpdateCallback - Invoked when STATE_UPDATE message is received (triggers state config refetch)
 * @param waitTimeUpdateCallback - Invoked when WAIT_TIME_UPDATE message is received with wait time data
 * @param initialSyncCallback - Optional. Invoked after WebSocket opens to fetch initial data via HTTP
 *
 * @example
 * setupRealtimeCallbacks(
 *   (order) => updateOrderInUI(order),
 *   () => refetchStateConfig(),
 *   (waitData) => updateWaitTime(waitData),
 *   () => fetchInitialOrders()
 * )
 */
export function setupRealtimeCallbacks(
  orderUpdateCallback: (order: OrderDetails) => void,
  stateUpdateCallback: () => void,
  waitTimeUpdateCallback: (waitTimeData: { waitTimeAverage: number, numberOfOrders: number }) => void,
  initialSyncCallback?: () => Promise<void>,
) {
  onOrderUpdate = orderUpdateCallback
  onStateUpdate = stateUpdateCallback
  onWaitTimeUpdate = waitTimeUpdateCallback
  onInitialSync = initialSyncCallback || null
}

/**
 * Convenience function that combines `setupRealtimeCallbacks()` and `connectWebSocketForStatus()`.
 *
 * This is the recommended way to establish a full realtime connection with message handling.
 * It registers all callbacks and initiates the WebSocket connection in a single call.
 *
 * The `initialSyncCallback` parameter is optional and typically used to fetch initial orders via HTTP
 * after WebSocket connects. This ensures data is available immediately while realtime updates take over.
 *
 * @param region - The application region for WebSocket connection
 * @param token - Authentication token for WebSocket connection
 * @param orderUpdateCallback - Invoked when ORDER_UPDATE message is received with order details
 * @param stateUpdateCallback - Invoked when STATE_UPDATE message is received (triggers state config refetch)
 * @param waitTimeUpdateCallback - Invoked when WAIT_TIME_UPDATE message is received with wait time data
 * @param initialSyncCallback - Optional. Invoked after WebSocket opens to fetch initial data via HTTP
 *
 * @example
 * connectWebSocketForRealtime(
 *   userRegion,
 *   authToken,
 *   (order) => updateOrderInUI(order),
 *   () => refetchStateConfig(),
 *   (waitData) => updateWaitTime(waitData),
 *   () => fetchInitialOrders()
 * )
 */
export function connectWebSocketForRealtime(
  region: AppRegion,
  token: string,
  orderUpdateCallback: (order: OrderDetails) => void,
  stateUpdateCallback: () => void,
  waitTimeUpdateCallback: (waitTimeData: { waitTimeAverage: number, numberOfOrders: number }) => void,
  initialSyncCallback?: () => Promise<void>,
) {
  setupRealtimeCallbacks(orderUpdateCallback, stateUpdateCallback, waitTimeUpdateCallback, initialSyncCallback)
  connectWebSocketForStatus(region, token)
}

/**
 * Returns the current WebSocket connection state.
 *
 * Useful for debugging and monitoring connection health. Includes connection status,
 * last activity timestamps, and connection intent.
 *
 * @returns Object containing:
 *   - isConnected: Whether WebSocket is currently open
 *   - lastEventSendTime: ISO timestamp of last sent message
 *   - lastReceivedTime: ISO timestamp of last received message
 *   - shouldBeConnected: Whether the connection should be active
 *
 * @example
 * const state = getWebSocketState()
 * if (!state.isConnected) {
 *   console.log('WebSocket disconnected, will attempt to reconnect')
 * }
 */
export function getWebSocketState() {
  return {
    isConnected: websocket?.readyState === WebSocket.OPEN,
    lastEventSendTime,
    lastReceivedTime,
    shouldBeConnected,
  }
}

/**
 * Returns detailed connection statistics for monitoring, debugging, and analytics.
 *
 * Provides comprehensive metrics about the WebSocket connection lifecycle including
 * attempt counts, success rates, failure reasons, and current uptime.
 *
 * Useful for monitoring dashboards, debugging connection issues, and analytics.
 *
 * @returns Object containing:
 *   - totalConnectionAttempts: Total number of connection attempts made
 *   - successfulConnections: Number of successful connections established
 *   - totalReconnections: Number of reconnection attempts
 *   - lastConnectionFailureReason: Reason for the last connection failure
 *   - connectionAttemptCount: Current backoff attempt count (resets on success)
 *   - isCurrentlyConnected: Whether WebSocket is currently open
 *   - currentUptime: Milliseconds since connection established (null if not connected)
 *
 * @example
 * const stats = getWebSocketConnectionStats()
 * console.log(`Connected: ${stats.isCurrentlyConnected}, Uptime: ${stats.currentUptime}ms`)
 * console.log(`Success rate: ${stats.successfulConnections}/${stats.totalConnectionAttempts}`)
 */
export function getWebSocketConnectionStats() {
  const stats = metrics.getStats()
  return {
    totalConnectionAttempts: stats.totalAttempts,
    successfulConnections: stats.successfulConnections,
    totalReconnections: stats.totalReconnections,
    lastConnectionFailureReason: stats.lastFailureReason,
    connectionAttemptCount: stats.backoffAttemptCount,
    isCurrentlyConnected: websocket?.readyState === WebSocket.OPEN,
    currentUptime: calculateDuration(stats.establishedTime, Date.now()),
  }
}

// Expose WebSocket stats on window for e2e tests and debugging
if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
  ;(window as any).getWebSocketConnectionStats = getWebSocketConnectionStats
}
